import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { Spinner } from "@performyard/py-components";
import { ScimLogModelFrontend } from "@performyard/service-contracts";

import { AdminContainer } from "../../../../../components/Admin";
import Breadcrumbs from "../../../../../components/Breadcrumbs";
import Pagination from "../../../../../components/Pagination";
import Spacer from "../../../../../components/Spacer";
import ScrollableTable from "../../../../../components/TableLite/TableLiteScrollable";
import Text from "../../../../../components/Text";
import ReadableScimFilterChips from "./ReadableScimFilterChips";
import ScimDetailsModal from "./ScimDetailsModal";
import ScimFilterPanel, { ScimLogsFilter } from "./ScimFilterPanel";
import { getScimLogTableColumns } from "./ScimLogTableColumns";

import { useGetScimTokenById, useScimLogs } from "../../../../../api/hooks";
import useDebouncedValue from "../../../../../hooks/useDebouncedValue";

import EmptySearchTableMessage from "../../../../../pageTemplates/InfoPageTemplate/EmptySearchTableMessage";
import StandardToolbarActionRow from "../../../../../pageTemplates/InfoPageTemplate/StandardToolbarActionRow";
import css from "../ScimLog.module.scss";

const ScimLog = (props: { pageSize: number; debounceLength: number }) => {
	const { pageSize, debounceLength } = props;
	const { scimId } = useParams();
	if (!scimId) {
		console.error("Missing SCIM ID or token");
		return null;
	}

	const [currentPage, setCurrentPage] = useState(1);
	const [searchValue, setSearchValue] = useState("");
	const [filters, setFilters] = useState<ScimLogsFilter>({
		date: { label: "Last 7 Days", value: "7" },
	});
	const [isFiltersPanelOpen, setIsFiltersPanelOpen] = useState(false);
	const [modalContent, setModalContent] = useState<ScimLogModelFrontend | null>(null);

	const debouncedSearchValue = useDebouncedValue(searchValue, debounceLength);

	// Reset pagination when filters or search value change
	useEffect(() => {
		setCurrentPage(1);
	}, [filters, debouncedSearchValue]);

	const { data: scimTokenData, isLoading: isScimTokenLoading } = useGetScimTokenById(scimId);
	const {
		data: scimLogs,
		totalCount,
		isLoading: isScimLogsLoading,
	} = useScimLogs(
		{
			scimToken: scimTokenData?.data?.token,
			scimId: scimId,
			limit: pageSize,
			skip: (currentPage - 1) * pageSize,
			userEmail: debouncedSearchValue,
			filters: filters,
		},
		{ enabled: !!scimTokenData?.data?.token }
	);
	const tableColumns = getScimLogTableColumns(setModalContent);

	const handleChangePage = (page: number) => {
		setCurrentPage(page);
	};

	const handleSearchInputChange = (value: string) => {
		setSearchValue(value);
	};

	const pageHeader = "SCIM Sync Log";
	const breadcrumbPages = [
		{
			pageID: "scim-api",
			label: "SCIM",
			url: "/administration/scim",
		},
		{
			pageID: "scim-sync-log",
			label: scimTokenData?.data?.name || "Sync Log",
			url: `/administration/scim/${scimId}/sync-log`,
		},
	];
	const filtersCount = Object.keys(filters)?.length;

	if (isScimTokenLoading || isScimLogsLoading) {
		return (
			<div className="flex h-full items-center justify-center">
				<Spinner size={20} />
			</div>
		);
	}

	return (
		<AdminContainer>
			<div className={css.mainContent}>
				<Breadcrumbs pages={breadcrumbPages} />
				<Text element="h1" size="heading-5" weight="bold" className={css.adminTitle} color="neutral-9">
					{pageHeader}
				</Text>

				<div className={css.tableContent}>
					<StandardToolbarActionRow
						searchInput={searchValue}
						onSearchInputChange={handleSearchInputChange}
						onFilterButtonPress={() => setIsFiltersPanelOpen(true)}
						searchPlaceholder="Search"
						activeFiltersCount={filtersCount}
					/>
					<Spacer height={1} />
					<ReadableScimFilterChips filters={filters} setFilters={(newFilters) => setFilters(newFilters)} />
					<Spacer height={2} />
					<ScrollableTable
						columns={tableColumns}
						rows={scimLogs}
						rowKey={"_id"}
						isLoading={isScimTokenLoading || isScimLogsLoading}
						emptyChildren={<EmptySearchTableMessage />}
					/>

					<ScimDetailsModal
						isOpen={modalContent !== null}
						handleClose={() => setModalContent(null)}
						scimLog={modalContent}
					/>
				</div>

				<Pagination
					currentPage={currentPage}
					pageCount={Math.ceil(totalCount / pageSize)}
					onClick={handleChangePage}
				/>
			</div>
			<ScimFilterPanel
				filters={filters}
				isOpen={isFiltersPanelOpen}
				onClose={() => setIsFiltersPanelOpen(false)}
				onApplyFilters={(newFilters) => setFilters(newFilters)}
			/>
		</AdminContainer>
	);
};

export default ScimLog;
